import { useEffect, useMemo, useState } from "react";

/**
 * BlogArticleTocLite — SAFE
 * - N'ajoute AUCUN style global ni règle de largeur.
 * - Ne modifie pas la typo, les marges ou le layout.
 * - Lit simplement les H2/H3 présents dans l'article et affiche un TOC.
 * - Ajoute une barre de progression de lecture fine (fixed top).
 *
 * Intégration conseillée:
 *  - Place un id sur le conteneur MDX: <div id="cw-article-body">...</div>
 *  - Monte <BlogArticleTocLite /> une seule fois dans la page /blog/[slug]
 */

export default function BlogArticleTocLite() {
  const [toc, setToc] = useState<Array<{ id: string; text: string; level: 2 | 3 }>>([]);
  const [progress, setProgress] = useState(0);

  // utilitaire slug
  const slugify = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 80);

  useEffect(() => {
    const body =
      document.getElementById("cw-article-body") ||
      document.querySelector("[data-blog-article]") ||
      document.querySelector("article");

    if (!body) return;

    // 1) Garantir des ids sur H2/H3 (sans rien styliser)
    const hs = Array.from(body.querySelectorAll<HTMLHeadingElement>("h2, h3"));
    hs.forEach((h) => {
      if (!h.id) h.id = slugify(h.textContent || "section");
    });

    // 2) Construire le TOC
    setToc(
      hs.map((h) => ({
        id: h.id,
        text: (h.textContent || "").trim(),
        level: h.tagName.toLowerCase() === "h2" ? 2 : 3,
      }))
    );

    // 3) Barre de progression lecture
    const total = () => Math.max(1, body.scrollHeight - window.innerHeight);
    const onScroll = () => {
      const scrolled = Math.min(Math.max(window.scrollY - body.getBoundingClientRect().top - window.scrollY + body.offsetTop, 0), total());
      setProgress(Math.min(100, Math.max(0, (scrolled / total()) * 100)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const hasToc = useMemo(() => toc.some((t) => t.level === 2), [toc]);

  return (
    <>
      {/* Barre de progression : aucune règle globale, juste un petit trait en haut */}
      <div className="fixed left-0 top-0 z-40 h-1 w-full bg-transparent pointer-events-none">
        <div
          className="h-full origin-left bg-emerald-500/80"
          style={{ transform: `scaleX(${progress / 100})` }}
        />
      </div>

      {/* TOC minimal : s'affiche seulement s'il y a des H2/H3 */}
      {hasToc && (
        <div className="mx-auto my-4 max-w-6xl px-4">
          <nav
            aria-label="Sommaire de l'article"
            className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
          >
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-300">
              Sommaire
            </div>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-300">
              {toc.map((i) => (
                <li key={i.id} className={i.level === 3 ? "pl-2 opacity-80" : ""}>
                  <a className="hover:text-white" href={`#${i.id}`}>
                    {i.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </>
  );
}